import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  LayoutDashboard, User, FileText, MessageCircle, Settings, LogOut,
  Lock, Save, Loader2, CheckCircle, Clock, AlertCircle, ChevronRight,
  Building2, Phone, Mail, Edit3, Receipt, BookOpen
} from "lucide-react";
import InvoicingTab from "@/components/portal/InvoicingTab";
import EcfGuideTab from "@/components/portal/EcfGuideTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  nuevo: { label: "Nuevo", color: "bg-blue-500/10 text-blue-400", icon: Clock },
  contactado: { label: "Contactado", color: "bg-yellow-500/10 text-yellow-400", icon: CheckCircle },
  negociacion: { label: "Negociacion", color: "bg-purple-500/10 text-purple-400", icon: AlertCircle },
  convertido: { label: "Convertido", color: "bg-green-500/10 text-green-400", icon: CheckCircle },
};

// ─── SIDEBAR ───
const PortalSidebar = ({ activeTab, setActiveTab, user, onLogout }) => (
  <aside className="hidden lg:flex w-60 bg-[#0a0f1a] border-r border-white/5 flex-col h-screen sticky top-0">
    <Link to="/" className="flex items-center gap-3 p-5 border-b border-white/5">
      <div className="w-9 h-9 bg-gradient-to-br from-[#3b82f6] to-[#06b6d4] flex items-center justify-center">
        <span className="text-white font-black text-sm">G</span>
      </div>
      <div>
        <span className="text-white font-bold text-xs tracking-wide">GLOBAL <span className="text-[#3b82f6]">METAFORA</span></span>
        <p className="text-[#94a3b8] text-[9px]">MI PORTAL</p>
      </div>
    </Link>

    <div className="p-4 border-b border-white/5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#3b82f6]/10 flex items-center justify-center text-[#3b82f6] font-bold text-sm">
          {user?.name?.charAt(0)?.toUpperCase() || "C"}
        </div>
        <div className="min-w-0">
          <p className="text-white text-sm font-medium truncate">{user?.name}</p>
          <p className="text-[#94a3b8] text-[10px] truncate">{user?.email}</p>
        </div>
      </div>
    </div>

    <nav className="flex-1 py-4 px-3 space-y-1">
      {[
        { id: "overview", icon: LayoutDashboard, label: "Resumen" },
        { id: "invoicing", icon: Receipt, label: "Facturacion e-CF" },
        { id: "ecf-guide", icon: BookOpen, label: "Guia e-CF" },
        { id: "demos", icon: FileText, label: "Mis Demos" },
        { id: "conversations", icon: MessageCircle, label: "Conversaciones" },
        { id: "profile", icon: User, label: "Mi Perfil" },
        { id: "security", icon: Lock, label: "Seguridad" },
      ].map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          data-testid={`portal-tab-${tab.id}`}
          className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-all ${
            activeTab === tab.id
              ? "bg-[#3b82f6]/10 text-[#3b82f6] border-l-2 border-[#3b82f6]"
              : "text-[#94a3b8] hover:text-white hover:bg-white/5"
          }`}
        >
          <tab.icon className="w-4 h-4" />
          {tab.label}
        </button>
      ))}
    </nav>

    <button
      onClick={onLogout}
      className="flex items-center gap-3 px-7 py-4 text-[#94a3b8] hover:text-red-400 transition-colors text-sm border-t border-white/5"
      data-testid="portal-logout-btn"
    >
      <LogOut className="w-4 h-4" /> Cerrar Sesion
    </button>
  </aside>
);

// ─── OVERVIEW ───
const OverviewTab = ({ user, demos }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-white text-xl font-bold">Bienvenido, {user?.name?.split(" ")[0]}</h2>
      <p className="text-[#94a3b8] text-sm">Aqui tienes un resumen de tu actividad</p>
    </div>
    <div className="grid sm:grid-cols-3 gap-4">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-5">
        <FileText className="w-8 h-8 text-[#3b82f6] mb-3" />
        <p className="text-2xl font-bold text-white">{demos.length}</p>
        <p className="text-[#94a3b8] text-sm">Demos Solicitadas</p>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card p-5">
        <CheckCircle className="w-8 h-8 text-emerald-400 mb-3" />
        <p className="text-2xl font-bold text-white">{demos.filter((d) => d.status === "convertido").length}</p>
        <p className="text-[#94a3b8] text-sm">Aprobadas</p>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card p-5">
        <Clock className="w-8 h-8 text-yellow-400 mb-3" />
        <p className="text-2xl font-bold text-white">{demos.filter((d) => d.status === "nuevo").length}</p>
        <p className="text-[#94a3b8] text-sm">Pendientes</p>
      </motion.div>
    </div>

    {demos.length > 0 && (
      <div className="glass-card p-5">
        <h3 className="text-white font-semibold mb-3">Ultimas solicitudes</h3>
        <div className="space-y-2">
          {demos.slice(0, 3).map((d, i) => {
            const st = STATUS_MAP[d.status] || STATUS_MAP.nuevo;
            return (
              <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded">
                <div>
                  <p className="text-white text-sm">{d.interest || "Demo general"}</p>
                  <p className="text-[#94a3b8] text-xs">{d.created_at ? new Date(d.created_at).toLocaleDateString("es") : ""}</p>
                </div>
                <span className={`text-xs px-2 py-1 font-medium ${st.color}`}>{st.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    )}

    <div className="grid sm:grid-cols-2 gap-4">
      <Link to="/demos" className="glass-card p-5 hover:border-[#3b82f6]/30 transition-colors group">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium">Explorar Demos</p>
            <p className="text-[#94a3b8] text-xs">Prueba nuestros sistemas</p>
          </div>
          <ChevronRight className="w-5 h-5 text-[#94a3b8] group-hover:text-[#3b82f6] transition-colors" />
        </div>
      </Link>
      <Link to="/#precios" className="glass-card p-5 hover:border-[#3b82f6]/30 transition-colors group">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium">Ver Planes</p>
            <p className="text-[#94a3b8] text-xs">Encuentra el plan ideal</p>
          </div>
          <ChevronRight className="w-5 h-5 text-[#94a3b8] group-hover:text-[#3b82f6] transition-colors" />
        </div>
      </Link>
    </div>
  </div>
);

// ─── DEMOS TAB ───
const DemosTab = ({ demos }) => (
  <div className="space-y-4">
    <h2 className="text-white text-xl font-bold">Mis Demos Solicitadas</h2>
    {demos.length > 0 ? (
      <div className="space-y-3">
        {demos.map((d, i) => {
          const st = STATUS_MAP[d.status] || STATUS_MAP.nuevo;
          const StIcon = st.icon;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="glass-card p-4"
              data-testid={`demo-item-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-[#3b82f6]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <StIcon className="w-4 h-4 text-[#3b82f6]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{d.interest || "Demo general"}</p>
                    <p className="text-[#94a3b8] text-xs">{d.source === "version_quiz" ? "Quiz de version" : "Solicitud directa"}</p>
                    {d.notes && <p className="text-[#94a3b8] text-xs mt-1">Nota: {d.notes}</p>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`text-xs px-2 py-1 font-medium ${st.color}`}>{st.label}</span>
                  <p className="text-[#94a3b8] text-[10px] mt-1">
                    {d.created_at ? new Date(d.created_at).toLocaleDateString("es", { day: "numeric", month: "short", year: "numeric" }) : ""}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    ) : (
      <div className="glass-card p-8 text-center">
        <FileText className="w-12 h-12 text-[#3b82f6]/20 mx-auto mb-3" />
        <p className="text-[#94a3b8]">Aun no has solicitado demos</p>
        <Link to="/demos" className="text-[#3b82f6] text-sm font-medium mt-2 inline-block hover:underline">
          Explorar demos disponibles
        </Link>
      </div>
    )}
  </div>
);

// ─── CONVERSATIONS TAB ───
const ConversationsTab = ({ conversations }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-white text-xl font-bold">Mis Conversaciones</h2>
      <Link to="/asesor" className="text-[#3b82f6] text-sm font-medium hover:underline">
        Nuevo chat
      </Link>
    </div>
    {conversations.length > 0 ? (
      <div className="space-y-3">
        {conversations.map((c, i) => (
          <motion.div
            key={c.id || i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass-card p-4 flex items-center gap-3"
          >
            <div className="w-9 h-9 bg-[#06b6d4]/10 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-4 h-4 text-[#06b6d4]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-medium truncate">{c.title || "Conversacion"}</p>
              <p className="text-[#94a3b8] text-xs">
                {c.updated_at ? new Date(c.updated_at).toLocaleDateString("es", { day: "numeric", month: "short" }) : ""}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    ) : (
      <div className="glass-card p-8 text-center">
        <MessageCircle className="w-12 h-12 text-[#06b6d4]/20 mx-auto mb-3" />
        <p className="text-[#94a3b8]">Sin conversaciones aun</p>
        <Link to="/asesor" className="text-[#3b82f6] text-sm font-medium mt-2 inline-block hover:underline">
          Hablar con el Asesor IA
        </Link>
      </div>
    )}
  </div>
);

// ─── PROFILE TAB ───
const ProfileTab = ({ user, onUpdate }) => {
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", company: user?.company || "" });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onUpdate(form);
      toast.success("Perfil actualizado");
    } catch {
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-white text-xl font-bold">Mi Perfil</h2>
      <div className="glass-card p-6">
        <form onSubmit={handleSave} className="space-y-4 max-w-md">
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
              <input value={user?.email || ""} disabled className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-[#94a3b8] text-sm cursor-not-allowed" />
            </div>
          </div>
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Nombre</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
              <input name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none" data-testid="profile-input-name" />
            </div>
          </div>
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Empresa</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
              <input name="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none" data-testid="profile-input-company" />
            </div>
          </div>
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Telefono</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
              <input name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none" data-testid="profile-input-phone" />
            </div>
          </div>
          <motion.button
            type="submit"
            disabled={saving}
            className="bg-[#3b82f6] text-white text-sm font-semibold px-6 py-2.5 flex items-center gap-2 disabled:opacity-50"
            whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
            data-testid="profile-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar Cambios
          </motion.button>
        </form>
      </div>
    </div>
  );
};

// ─── SECURITY TAB ───
const SecurityTab = ({ onChangePassword }) => {
  const [form, setForm] = useState({ current: "", newPw: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.newPw !== form.confirm) {
      setError("Las contrasenas no coinciden");
      return;
    }
    setSaving(true);
    try {
      await onChangePassword(form.current, form.newPw);
      toast.success("Contrasena actualizada");
      setForm({ current: "", newPw: "", confirm: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "Error al cambiar contrasena");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-white text-xl font-bold">Seguridad</h2>
      <div className="glass-card p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#3b82f6]" /> Cambiar Contrasena
        </h3>
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="security-error">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Contrasena actual</label>
            <input
              type="password"
              value={form.current}
              onChange={(e) => setForm({ ...form, current: e.target.value })}
              required
              className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
              data-testid="security-current-pw"
            />
          </div>
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Nueva contrasena</label>
            <input
              type="password"
              value={form.newPw}
              onChange={(e) => setForm({ ...form, newPw: e.target.value })}
              required
              minLength={6}
              className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
              data-testid="security-new-pw"
            />
          </div>
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Confirmar nueva contrasena</label>
            <input
              type="password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              required
              minLength={6}
              className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
              data-testid="security-confirm-pw"
            />
          </div>
          <motion.button
            type="submit"
            disabled={saving}
            className="bg-[#3b82f6] text-white text-sm font-semibold px-6 py-2.5 flex items-center gap-2 disabled:opacity-50"
            whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
            data-testid="security-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Cambiar Contrasena
          </motion.button>
        </form>
      </div>
    </div>
  );
};

// ─── MAIN PORTAL PAGE ───
const ClientPortal = () => {
  const { user, logout, updateProfile, changePassword } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [demos, setDemos] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [mobileTab, setMobileTab] = useState(false);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/auth/my-demos`).then((r) => setDemos(r.data.demos || [])).catch(() => {});
    axios.get(`${API}/auth/my-conversations`).then((r) => setConversations(r.data.conversations || [])).catch(() => {});
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <div className="min-h-screen bg-[#02040a] flex" data-testid="client-portal">
      <PortalSidebar activeTab={activeTab} setActiveTab={setActiveTab} user={user} onLogout={handleLogout} />

      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Mobile header */}
        <div className="lg:hidden h-14 bg-[#0a0f1a] border-b border-white/5 flex items-center justify-between px-4 sticky top-0 z-20">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-[#3b82f6] to-[#06b6d4] flex items-center justify-center">
              <span className="text-white font-black text-xs">G</span>
            </div>
            <span className="text-white font-bold text-xs">MI PORTAL</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-[#94a3b8] text-xs">{user?.name?.split(" ")[0]}</span>
            <button onClick={handleLogout} className="text-[#94a3b8] hover:text-red-400" data-testid="mobile-logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mobile tabs */}
        <div className="lg:hidden overflow-x-auto border-b border-white/5 bg-[#0a0f1a]">
          <div className="flex min-w-max">
            {[
              { id: "overview", label: "Resumen" },
              { id: "invoicing", label: "Facturacion" },
              { id: "ecf-guide", label: "Guia e-CF" },
              { id: "demos", label: "Demos" },
              { id: "conversations", label: "Chats" },
              { id: "profile", label: "Perfil" },
              { id: "security", label: "Seguridad" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id ? "text-[#3b82f6] border-b-2 border-[#3b82f6]" : "text-[#94a3b8]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
              {activeTab === "overview" && <OverviewTab user={user} demos={demos} />}
              {activeTab === "invoicing" && <InvoicingTab />}
              {activeTab === "ecf-guide" && <EcfGuideTab />}
              {activeTab === "demos" && <DemosTab demos={demos} />}
              {activeTab === "conversations" && <ConversationsTab conversations={conversations} />}
              {activeTab === "profile" && <ProfileTab user={user} onUpdate={updateProfile} />}
              {activeTab === "security" && <SecurityTab onChangePassword={changePassword} />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default ClientPortal;
